/* Example Usage:

// simple functional form
// use it as <Item moreData="XXX"/>
let Item = connect('Item', {storeMap: ['root.fancyData']}, ({fancyData, moreData}) => (
  <div>{fancyData}</div>
))

// class based form
class MyComponent extends React.Component {

  // mapping state from store to component props
  // this will trigger re-render on store changes
  // allowed types: string, array, mapping or callable
  static storeMap = (state, ownProps) => ({
    mappedColor: state.meta.color || 'red',
  })

  // dispatch action with this.props.someActionConstructor(color)
  static actionMap = {someActionConstructor, action2, go}

  // add this.props.dispatch(action) for generic action support
  // this.props.dispatch(Actions.someAction())
  static dispatchActions = true

  // setup ui state from defaults with read/write access
  // use case: user local settings suriving view changes
  // read: this.props.ui.*
  // write: this.props.setUI({selected: 'item2'})
  static uiDefaults = {selected: 'item1'}

  render() {
    let {ui, setUI, mappedColor, go} = this.props
    if (mappedColor) {
      let color = mappedColor
      return (
        <>
          <div onClick={() => go(color)}>Here we go in red</div>
          <div onClick={() => setUI({selected: 'item2'})}>change stuff in global store</div>
        </>
      )
    }
    // returning null prevents output of part
    return null
  }
}

export default connect('MyName', MyComponent)
*/

import React from 'react'

// Redux
import {connect as reduxConnect} from 'react-redux'
import {bindActionCreators} from 'redux'

import {
  get,
  fromPairs,
  entries,
  size,
  filterItems,
  mapItems,
  keys,
  has,
  dict,
  isObject,
  isArray,
  isNil,
  isFunction,
  isString,
  callIfFunc,
} from 'ldash'

import {createAction} from 'redux-actions'
import {compose, lifecycle, wrapDisplayName} from 'uix/utils/recompose'

import {Actions} from 'uix/registries'
import log from './logging'
import {STATE_KEY as UI_STATE_KEY, versionCheck, uiSetValue} from 'uix/persist/ui'

let sanityCheck = (componentName, compOrDescr) => {
  /* prevent some common mistakes */
  let isPart = componentName.startsWith('Part/')
  let onlyPartSupported = ['getGraphics', 'getChunkedUtils']

  if (!isPart && onlyPartSupported.some(n => compOrDescr[n])) {
    throw Error(`${componentName}: only (func)Part support: ${onlyPartSupported.join(', ')}`)
  }
  //~ if (componentName.includes('State')) log(componentName, compOrDescr)
}

// useful standard actions
export const DEFAULT_ACTION_MAP = {
  go: Actions.go,
  callApi: Actions.callApi,
  reduceApi: Actions.reduceApi,
}

// isReactComponent special property exists on React.Component since React 0.14
const isFunctionalComponent = c => isFunction(c) && !(c.prototype && c.prototype.isReactComponent)
const isClassComponent = c => !!(isFunction(c) && c.prototype && c.prototype.isReactComponent)

// a unique componentName is technically only important for `uiDefaults`
// but Minifier with name-mangling will kill at least compOrDescr.name if it's a react class comp
const connect = (componentName, compOrDescr, callable) => {
  /* connect a react class/functionial component to redux store
  this has been superseeded by react hooks + functionial components
  */
  if (!isString(componentName)) {
    throw Error(`componentName must be a string: ${componentName}`)
  }

  if (isNil(compOrDescr)) {
    log.error('undefined component (import error?): %o', componentName)
    compOrDescr = {}
    callable = ({}) => null
  }
  if (!isClassComponent(compOrDescr) && isFunction(compOrDescr)) {
    // shortest functional form: connect(name, func)
    callable = compOrDescr
    compOrDescr = {}
  } else if (isString(compOrDescr) || isArray(compOrDescr)) {
    // compOrDescr has storeMap rules in these cases
    compOrDescr = {storeMap: compOrDescr}
  }

  sanityCheck(componentName, compOrDescr)

  var {
    // XXX - formalize: supported part config (locally used)
    defaultActionMap = DEFAULT_ACTION_MAP,
    actionMap,
    dispatchActions,
    storeMap: mapAppState,
    uiDefaults,
    reduxOptions,
    ...remaining
  } = compOrDescr

  // ok, more sanity checks for content of compOrDescr
  // it's to easy to misspell storeMap (storemap)
  // we are only checking functional components - react classes have
  // to much static method noise
  if (isFunction(callable)) {
    let {
      // XXX - formalize: supported part config (non locally used)
      getGraphics,
      getChunkedUtils,
      defaultProps,
      partStyle,
      className,
      storeKey,
      wrapped,
      filters, // js code driven filter config
      ...moreRemaining
    } = remaining

    if (size(moreRemaining) > 0) {
      log.warn('unconsumed properties found on %o: %o', componentName, keys(moreRemaining))
    }
  }

  if (React.Component.isPrototypeOf(compOrDescr)) {
    // object oriented react style
    // compOrDescr has higher order signature:
    // Wrapper = props => <ComposedComponent {...props} />
    var component = compOrDescr
  } else if (isObject(compOrDescr)) {
    // functional react style
    if (!callable) {
      log.error('also need callable, got only a description %o', compOrDescr)
      throw Error(`got no callable for part description, got: ${compOrDescr}`)
    }
    var component = callable
  } else {
    throw Error(`connect did not understand shape of ${compOrDescr}`)
  }

  //~ log.debug('connect %O', componentName)
  if (!actionMap) {
    actionMap = {...defaultActionMap}
  } else {
    // copy
    actionMap = {...defaultActionMap, ...actionMap}
  }

  if (process.env.NODE_ENV !== 'production') {
    // check for import errors
    let invalid = filterItems(actionMap, (k, v) => !(typeof v === 'function'))
    if (invalid.length > 0) {
      log.error('invalid/undefined actions: %o', invalid.map(e => `${e[0]} [${typeof e[0]}]`))
    }
  }

  // we are going to build the storeMap function now
  let storeMap = null

  // first build app state function, either undefined, string, array, mapping or callable
  if (isString(mapAppState)) {
    // to array form
    mapAppState = [mapAppState]
  }
  if (Array.isArray(mapAppState)) {
    let names = mapAppState.filter(e => typeof e === 'string' && !e.startsWith('root.'))
    //~ log(names)
    if (size(names) != size(mapAppState)) {
      let msg = `<${componentName}> - filtered invalid values from storeMapping: ${mapAppState}`
      throw Error(msg)
      //~ log.error('filtered invalid values from storeMapping %o', mapAppState)
    }
    // transform array ['meta.request.PROPNAME'] to {PROPNAME: get(state, 'meta.request.PROPNAME')}
    mapAppState = state =>
      fromPairs(names.map(name => [name.split('.').slice(-1)[0], get(state, name)]))
  } else if (typeof mapAppState === 'object') {
    //~ log(isObject(mapAppState))
    let kvpairs = entries(mapAppState).filter(
      ([propname, path]) => isString(path) || isFunction(path)
    )
    if (size(kvpairs) != size(mapAppState)) {
      log.error('filtered invalid values from storeMapping %o', mapAppState)
    }
    // use lodash get or callable selector to get values from store
    mapAppState = state =>
      fromPairs(
        kvpairs.map(([propname, path]) => [
          propname,
          isString(path) ? get(state, path) : path(state),
        ])
      )
  }

  // ui state function and combine with app state
  if (uiDefaults) {
    // overwrite store key with __name__ (share a set of ui props over multiple components)
    let uiStoreKey = uiDefaults.__name__ ?? componentName

    actionMap['setUI'] = (vals, merge = true, version = null) =>
      uiSetValue(uiStoreKey, vals, merge, version)

    let mapUiState = (state, ownProps) => {
      let {value, version} = get(state, `${UI_STATE_KEY}.byId.${uiStoreKey}`, {})
      let values = versionCheck(value, version, uiDefaults, uiStoreKey, true)

      return {ui: values}
    }

    if (!mapAppState) {
      // only ui state
      storeMap = mapUiState
    } else {
      // combine ui state and app state
      storeMap = (state, ownProps) => ({
        ...mapAppState(state, ownProps),
        ...mapUiState(state, ownProps),
      })
    }
  } else {
    storeMap = mapAppState
  }

  let mapActions = actionMap
  if (dispatchActions) {
    // include generic this.props.dispatch
    mapActions = (dispatch, props) => ({
      dispatch,
      ...bindActionCreators(actionMap, dispatch),
    })
  }

  // set react displayName for unwrapped functional components (default=Unknown)
  // and for class based as well (probably not needed)
  component.displayName = componentName

  if (uiDefaults) {
    // will not trigger in SSR
    let lc = lifecycle({
      componentDidMount() {
        let {__needCreate__, __version__, ...ui} = this.props.ui
        if (__needCreate__) {
          // data from initial uiDefaults
          // no initialValue tracking
          this.props.setUI(ui, false, __version__)
        }
      },
    })
    component = compose(
      reduxConnect(storeMap, mapActions, null, reduxOptions),
      lc
    )(component)
    component.displayName = `Connect(${componentName})`
  } else {
    component = reduxConnect(storeMap, mapActions, null, reduxOptions)(component)
    component.displayName = `Connect(${componentName})`
  } // else return unwrapped component

  return component
}

export default connect
